import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { getRef2 } from "@/lib/procureApi";
import { STAGE, fmtDate, fmtInt } from "@/lib/format";
import { Path, Spinner, Lightning } from "@phosphor-icons/react";

export const Ref2Drawer = ({ ref2, snapshotId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !ref2) return;
    setLoading(true);
    setData(null);
    getRef2(ref2, snapshotId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, ref2, snapshotId]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        data-testid="ref2-drawer"
        className="w-full overflow-y-auto border-l border-[#E3E7EC] bg-[#FFFFFF] text-[#0F172A] sm:max-w-xl"
      >
        <SheetHeader className="space-y-1 text-left">
          <span className="pf-label flex items-center gap-1.5"><Path size={14} weight="bold" /> Reference 2 Journey</span>
          <SheetTitle className="font-mono text-2xl text-[#0F172A]">{ref2}</SheetTitle>
          <SheetDescription className="text-[#64748B]">
            Timeline of every order line sharing this Reference 2 across the OR → OQ → OF flow.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 py-12 text-[#64748B]">
            <Spinner size={18} className="animate-spin" /> Loading journey…
          </div>
        )}

        {data && (
          <>
            <div className="mt-4 grid grid-cols-3 gap-px border border-[#E3E7EC] bg-[#E3E7EC]">
              <div className="bg-[#FFFFFF] p-3">
                <div className="pf-label">Lines</div>
                <div className="mt-1 font-mono text-xl">{fmtInt(data.line_count)}</div>
              </div>
              <div className="bg-[#FFFFFF] p-3">
                <div className="pf-label">Production Drivers</div>
                <div className={`mt-1 font-mono text-xl ${data.critical_count ? "font-bold text-[#B91C1C]" : ""}`} data-testid="ref2-critical-count">{fmtInt(data.critical_count || 0)}</div>
              </div>
              <div className="bg-[#FFFFFF] p-3">
                <div className="pf-label">Stages</div>
                <div className="mt-1 flex gap-1 pt-1">
                  {["OR", "OQ", "OF"].map((s) => (
                    <span
                      key={s}
                      className="rounded-sm px-1.5 py-0.5 font-mono text-[11px] font-semibold"
                      style={
                        data.stages_present.includes(s)
                          ? { background: STAGE[s].color, color: "#FFFFFF" }
                          : { background: "#FFFFFF", color: "#CAD1DB", border: "1px solid #E3E7EC" }
                      }
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 border-l-2 border-[#E3E7EC] pl-5" data-testid="ref2-timeline">
              {data.items.map((it, i) => {
                const s = STAGE[it.order_type] || { color: "#64748B", name: it.order_type };
                return (
                  <div key={i} className="relative pb-6 last:pb-0" data-testid={`timeline-item-${i}`}>
                    <span
                      className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#FFFFFF]"
                      style={{ background: s.color }}
                    />
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-semibold" style={{ color: s.color }}>
                        {it.order_type}
                      </span>
                      <span className="font-mono text-xs text-[#64748B]">{fmtDate(it.order_date)}</span>
                    </div>
                    <div
                      data-critical={it.critical ? "true" : "false"}
                      className={`mt-1 rounded-sm border p-3 ${it.critical ? "border-[#B91C1C] bg-[#FDE2E4] font-bold [&_*]:!text-[#B91C1C]" : "border-[#E3E7EC] bg-[#FFFFFF]"}`}
                    >
                      {it.critical && <div className="mb-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider"><Lightning size={11} weight="fill" /> Production Driver</div>}
                      <div className="text-sm text-[#0F172A]">{it.description || "—"}</div>
                      {it.description2 && <div className="text-xs text-[#64748B]">{it.description2}</div>}
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] text-[#64748B]">
                        <span>Order # <span className="text-[#0F172A]">{it.order_number}-{it.line_number}</span></span>
                        <span>Item # <span className="text-[#0F172A]">{it.item_number || "—"}</span></span>
                        <span>Buyer <span className="text-[#0F172A]">{it.buyer}</span></span>
                        <span>Age <span className="text-[#0F172A]">{it.age_days ?? "—"}d</span></span>
                        <span className="col-span-2">Next <span className="text-[#0F172A]">{it.next_status_code || "—"}</span></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
